import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { auth, db } from "../firebase/config";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";

export default function EmployerTaskProgress() {
  const { employeeId } = useParams();
  const [categories, setCategories] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [employeeName, setEmployeeName] = useState("");

  // ✅ Fetch Company ID once user is authenticated
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          setLoading(true);
          setError(""); // ✅ Clear any previous errors

          const employerRef = doc(db, "users", user.uid);
          const employerDoc = await getDoc(employerRef);

          if (!employerDoc.exists() || !employerDoc.data().companyId) {
            throw new Error("Company ID not found.");
          }

          setCompanyId(employerDoc.data().companyId);
        } catch (err) {
          console.error("Error fetching company ID:", err.message);
          setError("Company information is missing. Please contact support.");
        } finally {
          setLoading(false);
        }
      } else {
        setError("Employer not authenticated.");
        setCompanyId(null); // Clear companyId to avoid issues
        setLoading(false);
      }
    });

    return () => unsubscribe(); // ✅ Cleanup listener on unmount
  }, []);

  useEffect(() => {
    const fetchCompanyId = async () => {
      try {
        setLoading(true);
        const employer = auth.currentUser;
        if (!employer) throw new Error("Employer not authenticated.");

        const employerRef = doc(db, "users", employer.uid);
        const employerDoc = await getDoc(employerRef);

        if (!employerDoc.exists() || !employerDoc.data().companyId) {
          throw new Error("Company ID not found.");
        }

        setCompanyId(employerDoc.data().companyId);
      } catch (err) {
        console.error("Error fetching company ID:", err.message);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyId();
  }, []);

  useEffect(() => {
    if (!companyId) return;

    const fetchCompletedCategories = async () => {
      try {
        setLoading(true);

        // Fetch employee details
        const employeeRef = doc(
          db,
          `companies/${companyId}/employees/${employeeId}`
        );
        const employeeDoc = await getDoc(employeeRef);
        if (employeeDoc.exists()) {
          setEmployeeName(employeeDoc.data().name);
        }

        // Fetch completed tasks under each category
        const categoriesRef = collection(
          db,
          `companies/${companyId}/employees/${employeeId}/onboarding_progress`
        );
        const categoriesSnapshot = await getDocs(categoriesRef);
        const categoryData = [];

        for (const categoryDoc of categoriesSnapshot.docs) {
          const category = { id: categoryDoc.id, ...categoryDoc.data() };

          // Filter tasks to only include completed ones
          const completedTasks = category.tasks.filter(
            (task) => task.status === "completed"
          );

          if (completedTasks.length > 0) {
            category.tasks = completedTasks;
            categoryData.push(category);
          }
        }

        setCategories(categoryData);
      } catch (err) {
        console.error("Error fetching completed tasks:", err.message);
        setError("Failed to fetch completed tasks.");
      } finally {
        setLoading(false);
      }
    };

    fetchCompletedCategories();
  }, [companyId, employeeId]);

  const handleApproveTask = async (categoryId, taskId) => {
    try {
      const categoryRef = doc(
        db,
        `companies/${companyId}/employees/${employeeId}/onboarding_progress`,
        categoryId
      );

      const categoryDoc = await getDoc(categoryRef);
      if (!categoryDoc.exists()) {
        alert("Category not found.");
        return;
      }

      let categoryData = categoryDoc.data();

      // ✅ Update the correct task
      categoryData.tasks = categoryData.tasks.map((task) =>
        task.taskId === taskId ? { ...task, reviewStatus: "approved" } : task
      );

      await updateDoc(categoryRef, { tasks: categoryData.tasks });

      setCategories((prev) =>
        prev.map((cat) =>
          cat.id === categoryId ? { ...cat, tasks: categoryData.tasks } : cat
        )
      );

      alert("Task approved successfully.");
    } catch (err) {
      console.error("Error approving task:", err.message);
      setError("Failed to approve task.");
    }
  };

  const handleRejectTask = async (categoryId, taskId) => {
    try {
      const categoryRef = doc(
        db,
        `companies/${companyId}/employees/${employeeId}/onboarding_progress`,
        categoryId
      );

      const categoryDoc = await getDoc(categoryRef);
      if (!categoryDoc.exists()) {
        alert("Category not found.");
        return;
      }

      let categoryData = categoryDoc.data();

      // ✅ Reset task for resubmission
      categoryData.tasks = categoryData.tasks.map((task) =>
        task.taskId === taskId
          ? {
              ...task,
              reviewStatus: "rejected",
              status: "pending", // Set back to pending
              assignedFields: Object.fromEntries(
                Object.keys(task.assignedFields).map((key) => [key, ""])
              ), // Clear values
            }
          : task
      );

      await updateDoc(categoryRef, { tasks: categoryData.tasks });

      setCategories((prev) =>
        prev.map((cat) =>
          cat.id === categoryId ? { ...cat, tasks: categoryData.tasks } : cat
        )
      );

      alert("Task rejected. Employee must resubmit.");
    } catch (err) {
      console.error("Error rejecting task:", err.message);
      setError("Failed to reject task.");
    }
  };

  return (
    <div>
      <h1>Onboarding Progress for {employeeName}</h1>
      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p style={{ color: "red" }}>{error}</p>
      ) : categories.length === 0 ? (
        <p>No completed tasks available.</p>
      ) : (
        <div>
          {categories
            //.filter((category) => category.reviewStatus !== "rejected")
            .map((category) => (
              <div key={category.id} style={{ marginBottom: "30px" }}>
                <h2>📂 {category.categoryName}</h2>
                <table border="1" width="100%">
                  <thead>
                    <tr>
                      <th>Task Name</th>
                      <th>Completed Fields</th>
                      <th>Completion Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {category.tasks.map((task) => (
                      <tr key={task.taskId}>
                        <td>{task.taskName}</td>
                        <td>
                          {Object.entries(task.assignedFields).map(
                            ([field, value]) => (
                              <div key={field}>
                                <strong>{field}:</strong> {value || "N/A"}
                              </div>
                            )
                          )}
                        </td>
                        <td>{new Date(task.completedAt).toLocaleString()}</td>
                        <td>
                          {task.fileUrl ? (
                            <iframe
                              src={task.fileUrl}
                              width="300"
                              height="400"
                              style={{ border: "1px solid #ccc" }}
                              title="Completed Document"
                            />
                          ) : (
                            "No Document"
                          )}
                        </td>
                        <td>
                          {task.reviewStatus === "approved" ? (
                            <span
                              style={{ color: "green", fontWeight: "bold" }}
                            >
                              ✅ Approved
                            </span>
                          ) : task.reviewStatus === "rejected" ? (
                            <span style={{ color: "red", fontWeight: "bold" }}>
                              ❌ Rejected
                            </span>
                          ) : (
                            <>
                              <button
                                onClick={() =>
                                  handleApproveTask(
                                    category.id,
                                    task.taskId,
                                    "approved"
                                  )
                                }
                                style={{
                                  marginRight: "10px",
                                  backgroundColor: "green",
                                  color: "white",
                                }}
                              >
                                ✅ Approve
                              </button>
                              <button
                                onClick={() =>
                                  handleRejectTask(
                                    category.id,
                                    task.taskId,
                                    "rejected"
                                  )
                                }
                                style={{
                                  backgroundColor: "red",
                                  color: "white",
                                }}
                              >
                                ❌ Reject
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
